/**
 * "Strava" in the profile's *Conexões* card (`D282`): connect, see who is
 * connected, disconnect — and the runtime that posts each finished workout.
 *
 * Connecting opens Strava's page in the system browser; Strava sends the
 * browser to the Worker, which keeps the tokens. The row re-reads
 * `GET /me/strava` whenever the app comes back to the front, which is how
 * it learns the connection happened.
 */

import type { AuthorizedFetcher } from "@repo/client/data/core";
import type { StravaStatusOutput } from "@repo/contract";
import {
  "native-release.strava_connect" as stravaConnect,
  "native-release.strava_connected" as stravaConnected,
  "native-release.strava_connected_plain" as stravaConnectedPlain,
  "native-release.strava_disconnect" as stravaDisconnect,
  "native-release.strava_hint" as stravaHint,
  "native-release.strava_label" as stravaLabel,
  "native-release.strava_waiting" as stravaWaiting,
} from "@repo/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useApi } from "../../data/api";
import { useDataQuery } from "../../data/query-client";
import { onAppActiveChange, openInBrowser, preferences } from "../../native";
import { Button } from "../../ui/controls";
import { useToast } from "../../ui/overlay";
import { appI18n } from "../auth/i18n";
import { sessionTitle } from "../plan/session-name";
import { workoutStore } from "../workout/store/instance";
import {
  createStravaExport,
  disconnectStrava,
  fetchStravaStatus,
  postStravaWorkout,
  STRAVA_CONNECTED_KEY,
  stravaConnectUrl,
} from "./strava";

const STRAVA_STATUS_KEY = ["connections", "strava"] as const;

const storage = {
  getItem: (key: string) => preferences.get(key),
  removeItem: (key: string) => preferences.remove(key),
  setItem: (key: string, value: string) => preferences.set(key, value),
};

/** Mirrors what the Worker said into the device flag the export reads. */
function remember(status: StravaStatusOutput): void {
  (status.available && status.connected
    ? preferences.set(STRAVA_CONNECTED_KEY, "on")
    : preferences.remove(STRAVA_CONNECTED_KEY)
  ).catch(() => undefined);
}

/** `GET /me/strava`, refetched when the app returns to the front. */
export function useStravaStatus() {
  const api = useApi();
  const client = useQueryClient();
  const query = useDataQuery({
    queryFn: async ({ signal }) => {
      const status = await fetchStravaStatus(api, signal);
      remember(status);
      return status;
    },
    queryKey: STRAVA_STATUS_KEY,
    staleTime: 60_000,
  });
  useEffect(
    () =>
      onAppActiveChange((active) => {
        if (active) {
          client
            .invalidateQueries({ queryKey: STRAVA_STATUS_KEY })
            .catch(() => undefined);
        }
      }),
    [client]
  );
  return query.data;
}

/**
 * Starts posting finished workouts to Strava; returns the stop. Called once
 * by `<ConnectionsRuntime />`. With the device flag off, each ended session
 * costs one Preferences read and no request.
 */
export function startStravaExport(api: AuthorizedFetcher): () => void {
  const exporter = createStravaExport({
    post: (workout) => postStravaWorkout(api, workout),
    storage,
    titleOf: (session) =>
      session.label === null ? stravaLabel() : sessionTitle(session.label),
  });
  const run = () => {
    exporter.update(workoutStore.getState().session).catch((error: unknown) => {
      console.warn("[connections] Strava export failed", error);
    });
  };
  let unsubscribe: (() => void) | null = null;
  let stopped = false;
  workoutStore.hydration.whenHydrated().then(() => {
    if (stopped) {
      return;
    }
    run();
    unsubscribe = workoutStore.subscribe(run);
  });
  return () => {
    stopped = true;
    unsubscribe?.();
  };
}

/** The row, for a Worker with Strava configured (`status.available`). */
export function StravaRow({ status }: { status: StravaStatusOutput }) {
  const api = useApi();
  const client = useQueryClient();
  const { notify } = useToast();
  const describeError = appI18n.useErrorMessage();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const refresh = useCallback(() => {
    client
      .invalidateQueries({ queryKey: STRAVA_STATUS_KEY })
      .catch(() => undefined);
  }, [client]);

  const connect = useCallback(() => {
    setBusy(true);
    stravaConnectUrl(api)
      .then(async (url) => {
        setWaiting(true);
        await openInBrowser(url);
      })
      .catch((error: unknown) => notify({ message: describeError(error) }))
      .finally(() => setBusy(false));
  }, [api, notify, describeError]);

  const disconnect = useCallback(() => {
    setBusy(true);
    disconnectStrava(api)
      .then(refresh)
      .catch((error: unknown) => notify({ message: describeError(error) }))
      .finally(() => setBusy(false));
  }, [api, notify, describeError, refresh]);

  return (
    <StravaRowView
      busy={busy}
      onConnect={connect}
      onDisconnect={disconnect}
      status={status}
      waiting={waiting}
    />
  );
}

/** What the row draws, from its state alone — the stories' door. */
export function StravaRowView({
  busy,
  onConnect,
  onDisconnect,
  status,
  waiting,
}: {
  busy: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  status: StravaStatusOutput;
  waiting: boolean;
}) {
  let line = waiting ? stravaWaiting() : stravaHint();
  if (status.connected) {
    line =
      status.athleteName === null || status.athleteName === undefined
        ? stravaConnectedPlain()
        : stravaConnected({ name: status.athleteName });
  }

  return (
    <div className="flex min-h-16 items-center gap-3 border-line border-t px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-base text-ink">{stravaLabel()}</p>
        <p className="mt-1 text-ink-3 text-sm">{line}</p>
      </div>
      {status.connected ? (
        <Button disabled={busy} onClick={onDisconnect} variant="secondary">
          {stravaDisconnect()}
        </Button>
      ) : (
        <Button disabled={busy} onClick={onConnect}>
          {stravaConnect()}
        </Button>
      )}
    </div>
  );
}
